import { BASE_API_URL } from 'core/infra/environment'
import { isForbiddenError } from 'core/infra/http/errors'
import { HttpClient } from 'core/infra/http/HttpClient'
import { SentryWrapper } from 'core/infra/sentry/SentryWrapper'
import { OpenDockReportsRepository } from 'managed-analytics/infra/OpenDockReportsRepository'

export type OpenDockReportsFactoryDependencies = {
  openDockReportsRepository: OpenDockReportsRepository
}

function createRepository() {
  const httpClient = new HttpClient(BASE_API_URL)
  return new OpenDockReportsRepository({ httpClient })
}

function withSentry<T>(work: () => Promise<T>): Promise<T> {
  return work().catch(error => {
    if (!isForbiddenError(error)) {
      SentryWrapper.log(error as Error)
    }
    throw error
  })
}

export function getOpenDockReportsFactory({
  openDockReportsRepository,
}: OpenDockReportsFactoryDependencies) {
  return async function (config?: { signal?: AbortSignal }) {
    return withSentry(() => openDockReportsRepository.getReports(config))
  }
}

export function getLatestOpenDockReportDownloadFactory({
  openDockReportsRepository,
}: OpenDockReportsFactoryDependencies) {
  return async function (config?: { signal?: AbortSignal }) {
    return withSentry(() => openDockReportsRepository.getLatestDownload(config))
  }
}

export function getOpenDockReportDownloadFactory({
  openDockReportsRepository,
}: OpenDockReportsFactoryDependencies) {
  return async function (uuid: string, config?: { signal?: AbortSignal }) {
    return withSentry(() => openDockReportsRepository.getDownload(uuid, config))
  }
}

export function getOpenDockReportPdfFactory({
  openDockReportsRepository,
}: OpenDockReportsFactoryDependencies) {
  return async function (uuid: string, config?: { signal?: AbortSignal }) {
    return withSentry(() => openDockReportsRepository.getFile(uuid, config))
  }
}

export function getOpenDockReports(config?: { signal?: AbortSignal }) {
  const get = getOpenDockReportsFactory({
    openDockReportsRepository: createRepository(),
  })
  return get(config)
}

export function getLatestOpenDockReportDownload(config?: { signal?: AbortSignal }) {
  const get = getLatestOpenDockReportDownloadFactory({
    openDockReportsRepository: createRepository(),
  })
  return get(config)
}

export function getOpenDockReportDownload(uuid: string, config?: { signal?: AbortSignal }) {
  const get = getOpenDockReportDownloadFactory({
    openDockReportsRepository: createRepository(),
  })
  return get(uuid, config)
}

export function getOpenDockReportPdf(uuid: string, config?: { signal?: AbortSignal }) {
  const get = getOpenDockReportPdfFactory({
    openDockReportsRepository: createRepository(),
  })
  return get(uuid, config)
}
